import { Flex, Grid, Skeleton } from 'antd';
import Table from 'antd/es/table';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  applyDataTableColumnWidth,
  resolveDataTableColumnWidth,
  resolveDataTableScrollX,
} from '@/UI/DataTable/columnWidth';
import {
  DataTableResizableHeaderCell,
  toDataTableHeaderCellProps,
} from '@/UI/DataTable/DataTableResizableHeaderCell';
import Empty from '@/UI/Empty/Empty';

import type {
  DataTableColumn,
  DataTableProps,
  DataTableSort,
} from '@/UI/DataTable/types';
import type { ColumnsType } from 'antd/es/table';
import type { SorterResult } from 'antd/es/table/interface';
import type { ComponentProps } from 'react';

const INITIAL_SKELETON_ROW_COUNT = 6;

const TABLE_COMPONENTS = {
  header: { cell: DataTableResizableHeaderCell },
};

type DataTableSkeletonRow = {
  __dataTableSkeletonKey: string;
};

function renderSkeletonCell<T>(
  column: DataTableColumn<T>,
  columnIndex: number,
) {
  if (column.fixed === 'left') {
    return (
      <Flex align="center" gap="small">
        <Skeleton.Avatar active size={32} />
        <Flex vertical flex={1}>
          <Skeleton
            active
            title={false}
            paragraph={{ rows: 2, width: ['72%', '48%'] }}
          />
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex justify={column.align === 'right' ? 'flex-end' : column.align}>
      <Skeleton.Input
        active
        size="small"
        style={{ width: `${48 + ((columnIndex * 17) % 32)}%` }}
      />
    </Flex>
  );
}

function DataTable<T>({
  columns,
  rows,
  rowKey,
  layout,
  onLayoutChange,
  loading,
  sort,
  onSortChange,
  selection,
  expandable,
  pagination = false,
  scrollY,
  virtual,
  onRowClick,
  getRowTestId,
  emptyDescription,
  'data-testid': dataTestId,
}: DataTableProps<T>) {
  const { t } = useTranslation();
  const hidden = useMemo(() => new Set(layout.hidden), [layout.hidden]);
  const orderIndex = useMemo(
    () => new Map(layout.order.map((id, index) => [id, index])),
    [layout.order],
  );
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  const visibleColumns = useMemo(
    () =>
      columns
        .filter(
          (column) =>
            column.isAvailable !== false &&
            (column.locked || !hidden.has(column.id)),
        )
        .sort(
          (a, b) =>
            (orderIndex.get(a.id) ?? columns.length) -
            (orderIndex.get(b.id) ?? columns.length),
        ),
    [columns, hidden, orderIndex],
  );
  const showInitialSkeleton = Boolean(loading && rows.length === 0);
  const skeletonRows = useMemo(
    () =>
      Array.from(
        { length: INITIAL_SKELETON_ROW_COUNT },
        (_, index): DataTableSkeletonRow => ({
          __dataTableSkeletonKey: `data-table-skeleton-${index}`,
        }),
      ) as unknown as T[],
    [],
  );

  // A drag reads the layout it started from, so the resize callback must stay
  // stable across the frames it fires on.
  const layoutRef = useRef(layout);
  useEffect(() => {
    layoutRef.current = layout;
  }, [layout]);

  const handleColumnResize = useCallback(
    (columnId: string, width: number) => {
      onLayoutChange(
        applyDataTableColumnWidth(layoutRef.current, columnId, width),
      );
    },
    [onLayoutChange],
  );

  const antdColumns = useMemo(
    (): ColumnsType<T> =>
      visibleColumns.map((column, columnIndex) => {
        const width = resolveDataTableColumnWidth(column, layout.widths);
        return {
          key: column.id,
          title: column.title,
          width,
          align: column.align,
          fixed: isMobile ? false : column.fixed,
          sorter: showInitialSkeleton
            ? false
            : column.sortable === false
              ? false
              : column.sortKey && onSortChange
                ? true
                : column.clientSorter,
          sortOrder:
            column.sortable !== false && sort && sort.key === column.sortKey
              ? sort.direction
              : undefined,
          showSorterTooltip: false,
          ellipsis: column.ellipsis ?? false,
          render: (_: unknown, row: T) =>
            showInitialSkeleton
              ? renderSkeletonCell(column, columnIndex)
              : column.render(row),
          onHeaderCell: () =>
            toDataTableHeaderCellProps({
              columnId: column.id,
              columnWidth: width,
              columnMinWidth: column.minWidth,
              onColumnResize: handleColumnResize,
              ...(column.headerAlign
                ? { style: { textAlign: column.headerAlign } }
                : {}),
            }),
        };
      }),
    [
      visibleColumns,
      layout.widths,
      sort,
      showInitialSkeleton,
      onSortChange,
      handleColumnResize,
      isMobile,
    ],
  );

  const scrollX = useMemo(
    () => resolveDataTableScrollX(visibleColumns, layout.widths),
    [visibleColumns, layout.widths],
  );

  const handleChange: NonNullable<
    ComponentProps<typeof Table<T>>['onChange']
  > = (_pagination, _filters, sorter, extra) => {
    // Paginate/filter also fire Table.onChange; only sort must update sort state.
    if (!onSortChange || extra.action !== 'sort') return;
    const active: SorterResult<T> = Array.isArray(sorter) ? sorter[0] : sorter;
    if (!active.order) {
      onSortChange(null);
      return;
    }
    const column = columns.find(({ id }) => id === active.columnKey);
    if (!column || column.sortable === false || !column.sortKey) return;
    const nextSort: DataTableSort = {
      key: column.sortKey,
      direction: active.order,
    };
    onSortChange(nextSort);
  };

  return (
    <Table<T>
      data-testid={dataTestId}
      rowKey={
        showInitialSkeleton
          ? (row) =>
              (row as unknown as DataTableSkeletonRow).__dataTableSkeletonKey
          : rowKey
      }
      columns={antdColumns}
      components={TABLE_COMPONENTS}
      dataSource={showInitialSkeleton ? skeletonRows : rows}
      loading={loading && !showInitialSkeleton}
      pagination={showInitialSkeleton ? false : pagination}
      tableLayout={scrollX === 'max-content' ? 'auto' : 'fixed'}
      scroll={{
        x: scrollX,
        y: scrollY,
        // Server-driven pages already replace rows; avoid yanking the body on paginate.
        scrollToFirstRowOnChange: false,
      }}
      expandable={expandable}
      virtual={virtual}
      rowSelection={
        selection && !showInitialSkeleton
          ? {
              selectedRowKeys: selection.selectedRowKeys,
              onChange: selection.onChange,
              getCheckboxProps: selection.getCheckboxProps,
              preserveSelectedRowKeys:
                selection.preserveSelectedRowKeys ?? true,
              hideSelectAll: selection.hideSelectAll,
              fixed: true,
            }
          : selection
            ? {
                selectedRowKeys: [],
                onChange: () => undefined,
                getCheckboxProps: () => ({ disabled: true }),
                hideSelectAll: selection.hideSelectAll,
                fixed: true,
              }
            : undefined
      }
      onChange={showInitialSkeleton ? undefined : handleChange}
      locale={{
        emptyText: (
          <Empty size="small" description={emptyDescription ?? t('NO_DATA')} />
        ),
      }}
      onRow={
        !showInitialSkeleton && (onRowClick || getRowTestId)
          ? (row) => ({
              ...(getRowTestId
                ? { 'data-testid': getRowTestId(row) }
                : undefined),
              ...(onRowClick
                ? {
                    onClick: () => onRowClick(row),
                    onKeyDown: (event: React.KeyboardEvent) => {
                      if (event.key !== 'Enter') return;
                      event.preventDefault();
                      onRowClick(row);
                    },
                    tabIndex: 0,
                  }
                : undefined),
            })
          : undefined
      }
    />
  );
}

export { DataTable };
